/**
 * SquadSwapModal — Last-minute squad swap (post-lock / mid-match).
 *
 * Shows both teams' locked squads. Tapping the swap icon on a squad player
 * opens a picker with the team's pool minus the current squad; choosing a
 * replacement calls POST /squads/{team_id}/swap-player. Backend rejects swap
 * if the outgoing player has any ball reference or is on-field.
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

export default function SquadSwapModal({ eventId, fixtureId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // { team_id, out_player } — when set, shows the replacement picker.
  const [picking, setPicking] = useState(null);

  const fetchSquads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load squads');
    } finally {
      setLoading(false);
    }
  }, [eventId, fixtureId]);

  useEffect(() => { fetchSquads(); }, [fetchSquads]);

  const handleSwap = async (teamId, outId, inId) => {
    setSaving(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}/swap-player`,
        { out_player_pool_id: outId, in_player_pool_id: inId }
      );
      toast.success('Squad player swapped');
      setPicking(null);
      await fetchSquads();
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to swap player');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
        <div className="bg-white rounded-2xl p-6 shadow-xl">Loading squads...</div>
      </div>
    );
  }

  // Replacement picker view
  if (picking) {
    const teamRoster = data.roster_by_team?.[picking.team_id] || [];
    const team = (data.teams || []).find(t => t.team_id === picking.team_id);
    const squadIds = new Set((team?.players || []).map(p => p.player_pool_id));
    const available = teamRoster.filter(p => !squadIds.has(p.player_pool_id));
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]" data-testid="squad-swap-picker" onClick={() => setPicking(null)}>
        <div className="bg-white rounded-2xl p-5 w-full max-w-sm mx-4 shadow-xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-bold text-lg text-[#2B3A67]">Pick replacement</h3>
              <p className="text-xs text-gray-500">
                Out: <span className="font-semibold text-gray-700">{picking.out_player.player_name}</span>
                {' · '}from {team?.team_name} pool
              </p>
            </div>
            <button onClick={() => setPicking(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>
          {available.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-6">
              No remaining players in this team's pool.
            </p>
          ) : (
            <div className="space-y-1.5 overflow-y-auto pr-1">
              {available.map(p => (
                <button
                  key={p.player_pool_id}
                  onClick={() => handleSwap(picking.team_id, picking.out_player.player_pool_id, p.player_pool_id)}
                  disabled={saving}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-[#3AAFA9] hover:bg-[#3AAFA9]/5 disabled:opacity-50"
                  data-testid={`swap-pick-${p.player_pool_id}`}
                >
                  {p.player_photo ? (
                    <img src={p.player_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <span className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                      {p.player_name?.[0] || '?'}
                    </span>
                  )}
                  <span className="font-medium text-gray-800 truncate flex-1">{p.player_name}</span>
                  {p.playing_role && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 flex-shrink-0">{p.playing_role}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main squads list view
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]" data-testid="squad-swap-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-md mx-4 shadow-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-lg text-[#2B3A67]">Manage Squads</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Last-minute swap. Replacements come from the team's pool. Players who have already batted, bowled or fielded can't be swapped — use 'Replace batsman' on the on-field banner instead.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0 ml-2"
            data-testid="squad-swap-close"
          >×</button>
        </div>

        <div className="overflow-y-auto pr-1 space-y-4">
          {(data.teams || []).map(team => (
            <div key={team.team_id} className="border border-gray-100 rounded-xl p-3" data-testid={`squad-swap-team-${team.team_id}`}>
              <div className="flex items-center gap-2 mb-2">
                {team.team_logo_url && (
                  <img src={team.team_logo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                )}
                <h4 className="font-bold text-sm text-[#2B3A67] flex-1">{team.team_name}</h4>
                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                  {(team.players || []).length} players
                </span>
              </div>
              {(team.players || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic py-2 text-center">Squad not yet selected.</p>
              ) : (
                <div className="space-y-1">
                  {team.players.map(p => (
                    <div
                      key={p.player_pool_id}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-gray-50 text-sm"
                      data-testid={`squad-swap-row-${p.player_pool_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {p.player_photo ? (
                          <img src={p.player_photo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                            {p.player_name?.[0] || '?'}
                          </span>
                        )}
                        <span className="truncate font-medium text-gray-800">{p.player_name}</span>
                        {p.is_captain && (
                          <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1 rounded">C</span>
                        )}
                        {p.is_vice_captain && (
                          <span className="text-[9px] font-bold bg-sky-100 text-sky-700 px-1 rounded">VC</span>
                        )}
                        {p.is_wicketkeeper && (
                          <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1 rounded">WK</span>
                        )}
                      </div>
                      <button
                        onClick={() => setPicking({ team_id: team.team_id, out_player: p })}
                        disabled={saving}
                        className="text-amber-600 hover:text-amber-700 text-xs font-medium flex-shrink-0 disabled:opacity-50"
                        title="Swap with another pool player"
                        data-testid={`swap-out-${p.player_pool_id}`}
                      >
                        <i className="ph-bold ph-arrows-clockwise"></i> Swap
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="mt-3 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
          data-testid="squad-swap-done"
        >Done</button>
      </div>
    </div>
  );
}
